import {
  type CalculationInfoResponse,
  ContractLoanTypeEnum,
} from '@kk/api/modules/customerPortal';
import {
  formatBasisPointsToPercentage,
  formatCurrency,
  formatPercentage,
} from '@kk/shared/utils/numbers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type InfoRow = {
  id: string;
  label: string;
  value: string | undefined;
  tooltip?: string;
};

export function usePriceInfoRows(calculation: CalculationInfoResponse) {
  const { t } = useTranslation('ui');
  return useMemo(() => {
    const rows: InfoRow[] = [];

    rows.push({
      id: 'currency',
      label: t('loan.currency'),
      value: calculation.currency,
    });

    rows.push({
      id: 'currentRate',
      label: t('loan.currentRate'),
      tooltip: t('loan.currentRate.tooltip'),
      value: formatPercentage(
        calculation.loanProduct === ContractLoanTypeEnum.KKcibor
          ? calculation.interestRatePlusSpread
          : calculation.interestRate,
      ),
    });

    if (calculation.loanProduct === ContractLoanTypeEnum.KKcibor) {
      rows.push({
        id: 'spread',
        label: t('loan.spread.cibor'),
        value: formatBasisPointsToPercentage(calculation.spread),
      });
    }

    rows.push({
      id: 'marginRate',
      label: t('loan.marginRate'),
      value: formatPercentage(calculation.marginRate),
    });

    rows.push({
      id: 'initialCosts',
      label: t('loan.initialCosts'),
      value: formatCurrency(0, { showZero: true }),
    });

    if (calculation.loanProduct !== ContractLoanTypeEnum.Byggekredit) {
      rows.push({
        id: 'firstPayment',
        label: t('loan.firstPayment'),
        value: formatCurrency(calculation.firstPayment),
      });
    }

    return rows;
  }, [calculation, t]);
}

export default usePriceInfoRows;
